import React, { useState } from 'react';
import { 
  ThemeProvider, 
  createTheme, 
  CssBaseline, 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  Box, 
  Switch, 
  FormControlLabel, 
  MenuItem, 
  Select 
} from "@mui/material";
import { 
  Brightness4 as DarkModeIcon, 
  Brightness7 as LightModeIcon 
} from "@mui/icons-material";
import { Outlet } from 'react-router-dom';
import { useTranslation } from "react-i18next";

function AppLayout() {
  const { t, i18n } = useTranslation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [language, setLanguage] = useState(i18n.language);

  // Create dynamic theme
  const theme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: isDarkMode ? "#90caf9" : "#002C3E",
      },
      background: {
        default: isDarkMode ? "#121212" : "#f4f4f4",
        paper: isDarkMode ? "#1d1d1d" : "#ffffff",
      },
    },
    typography: {
      fontFamily: "Roboto, Arial, sans-serif",
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            position: "static",
          },
        },
      },
    },
  });

  // Toggle theme mode
  const handleThemeToggle = () => {
    setIsDarkMode((prev) => !prev);
  };

  // Handle language change
  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <AppBar position="static">
          <Container maxWidth="xl">
            <Toolbar>
              <Box sx={{ pt: 1, flexGrow: 1 }}>
                <img
                  src="../assets/aludra-white-horizontal.png"
                  alt="Aludra Logo"
                  width={150}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Select
                  value={language}
                  onChange={handleLanguageChange}
                  sx={{
                    color: "white",
                    "& .MuiSelect-icon": { color: "white" },
                    mr: 2,
                  }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Español</MenuItem>
                </Select>

                <FormControlLabel
                  control={
                    <Switch
                      checked={isDarkMode}
                      onChange={handleThemeToggle}
                      color="default"
                    />
                  }
                  label={isDarkMode ? <DarkModeIcon /> : <LightModeIcon />}
                />
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        {/* Main Content Area */}
        <Container
          maxWidth="md"
          sx={{
            mt: 4,
            mb: 4,
            flex: 1,
          }}
        >
          <Outlet /> {/* This is where child routes will be rendered */}
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            mt: "auto",
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default AppLayout;